<template>
  <div ref="sceneContainer" class="scene-container">
    <canvas ref="canvas"></canvas>
  </div>
</template>

<script>
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader';
import { SkeletonHelper } from 'three';
import { CCDIKSolver } from 'three/examples/jsm/animation/CCDIKSolver';
import AOManager from '@/utils/AOManager';

export default {
  name: 'ThreeScene',
  data() {
    return {
      // Three.js 变量
      scene: null,
      camera: null,
      renderer: null,
      model: null,
      controls: null,
      animationFrameId: null,
      mixer: null,
      skeletonHelper: null,
      ikSolver: null,
      animations: [],
      clock: new THREE.Clock(),

      // AO管理器
      aoManager: null,

      // 自动恢复视角相关
      autoResetTimer: null,
      AUTO_RESET_DELAY: 15000, // 15秒

      // 存储骨骼007的初始位置和当前高度
      bone007InitialX: null,
      bone007InitialY: null,
      bone007height: 3,

      // 天轮控制相关
      wheelObject: null,
      lastLiftHeight: 3,
      wheelRotation: 0,

      // 自定义lookAt约束系统
      lookAtConstraints: [],

      // 渲染性能优化
      frameCount: 0,
      needsRender: true,
      isAnimating: false,
      lastCameraPosition: new THREE.Vector3(),
      lastCameraTarget: new THREE.Vector3(),
      renderTimeout: null
    }
  },
  watch: {
    '$store.getters.isDayMode'(newVal) {
      this.updateDayNightMode(newVal);
      this.requestRender();
    },

    '$store.getters.ladderUp'(newVal) {
      this.updateLadderPosition(newVal);
      this.requestRender();
    },

    '$store.getters.platformRotation'(newVal) {
      this.updatePlatformRotation(newVal);
      this.requestRender();
    },

    '$store.getters.armExtension'(newVal) {
      this.updateArmExtension(newVal);
      this.requestRender();
    },

    '$store.getters.liftHeight'(newVal) {
      this.updateLiftHeight(newVal);
      this.requestRender();
      // 检查参数重置，重置天轮旋转
      if (this.$store.getters.liftHeight === 3 && this.$store.getters.armExtension === 6 && this.$store.getters.platformRotation === 0) {
        this.resetWheelRotation();
      }
    },

    '$store.getters.autoResetCamera'(newVal) {
      if (newVal) {
        this.startAutoResetTimer();
      } else {
        this.clearAutoResetTimer();
      }
    },

    '$store.getters.enableAO'(newVal) {
      this.updateAOEffect(newVal);
      this.requestRender();
    },

    '$store.getters.gtaoOutput'() {
      this.updateGTAOParams();
      this.requestRender();
    },

    '$store.getters.blendIntensity'() {
      this.updateGTAOParams();
      this.requestRender();
    },

    '$store.getters.aoRadius'() {
      this.updateGTAOParams();
      this.requestRender();
    },

    '$store.getters.distanceExponent'() {
      this.updateGTAOParams();
      this.requestRender();
    },

    '$store.getters.thickness'() {
      this.updateGTAOParams();
      this.requestRender();
    },

    '$store.getters.distanceFallOff'() {
      this.updateGTAOParams();
      this.requestRender();
    },

    '$store.getters.scale'() {
      this.updateGTAOParams();
      this.requestRender();
    },

    '$store.getters.aoSamples'() {
      this.updateGTAOParams();
      this.requestRender();
    },

    '$store.getters.bone007AnimationPlaying'(newVal) {
      this.controlBone007Animation(newVal);
    },

    '$store.getters.bone007AnimationPaused'(newVal) {
      if (this.mixer && this.animations.length > 0) {
        const action = this.mixer.clipAction(this.animations[0]);
        if (newVal) {
          action.paused = true;
        } else if (this.$store.getters.bone007AnimationPlaying) {
          action.paused = false;
        }
      }
    },

    '$store.getters.dayModeColors': {
      handler() {
        if (this.$store.getters.isDayMode) {
          this.updateSceneColors();
          this.requestRender();
        }
      },
      deep: true
    },

    '$store.getters.nightModeColors': {
      handler() {
        if (!this.$store.getters.isDayMode) {
          this.updateSceneColors();
          this.requestRender();
        }
      },
      deep: true
    }
  },
  mounted() {
    this.initScene();
    this.startAnimation();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    
    if (this.renderTimeout) {
      clearTimeout(this.renderTimeout);
    }
    
    if (this.mixer) {
      this.mixer.stopAllAction();
      this.mixer = null;
    }
    
    if (this.skeletonHelper) {
      this.scene?.remove(this.skeletonHelper);
      this.skeletonHelper = null;
    }
    
    this.ikSolver = null;
    
    // 清理lookAt约束系统
    this.lookAtConstraints = [];
    
    // 清理天轮对象引用
    this.wheelObject = null;
    
    // 清理AO管理器
    if (this.aoManager) {
      this.aoManager.dispose();
      this.aoManager = null;
    }
    
    this.renderer?.dispose();
    this.scene?.clear();
    this.controls?.dispose();
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    // 控制骨骼007动画播放
    controlBone007Animation(shouldPlay) {
      if (!this.mixer || !this.animations.length) return;
      
      const action = this.mixer.clipAction(this.animations[0]);
      
      if (shouldPlay) {
        action.reset();
        action.setLoop(THREE.LoopRepeat);
        action.play();
      } else {
        action.stop();
        action.reset();
        action.time = 0;
        action.weight = 1;
        this.mixer.setTime(0);
        this.mixer.update(0);
        this.requestRender();
      }
    },

    // 自动恢复视角功能
    startAutoResetTimer() {
      if (!this.$store.getters.autoResetCamera) return;
      
      this.clearAutoResetTimer();
      this.autoResetTimer = setTimeout(() => {
        this.resetCameraToFinalPosition();
      }, this.AUTO_RESET_DELAY);
    },

    clearAutoResetTimer() {
      if (this.autoResetTimer) {
        clearTimeout(this.autoResetTimer);
        this.autoResetTimer = null;
      }
    },

    resetCameraToFinalPosition() {
      if (!this.camera || !this.controls) return;
      
      const finalPosition = new THREE.Vector3(27, 1.5, 27);
      const finalTarget = new THREE.Vector3(8, 12, 0);
      
      const startPosition = this.camera.position.clone();
      const startTarget = this.controls.target.clone();
      
      const startTime = Date.now();
      const duration = 2000; // 2秒动画
      
      const animateReset = () => {
        const elapsedTime = Date.now() - startTime;
        const progress = Math.min(elapsedTime / duration, 1);
        
        const easeProgress = this.easeInOutCubic(progress);
        
        this.camera.position.lerpVectors(startPosition, finalPosition, easeProgress);
        this.controls.target.lerpVectors(startTarget, finalTarget, easeProgress);
        this.controls.update();
        
        if (progress < 1) {
          requestAnimationFrame(animateReset);
        }
      }
      
      animateReset();
    },

    // 更新AO效果
    updateAOEffect(enabled) {
      if (this.aoManager) {
        this.aoManager.setEnabled(enabled);
      }
    },

    // 更新GTAO参数
    updateGTAOParams() {
      if (this.aoManager) {
        // 更新输出模式
        this.aoManager.updateOutput(this.$store.getters.gtaoOutput);
        
        // 更新混合强度
        this.aoManager.updateBlendIntensity(this.$store.getters.blendIntensity);
        
        // 更新AO参数
        const aoParameters = {
          radius: this.$store.getters.aoRadius,
          distanceExponent: this.$store.getters.distanceExponent,
          thickness: this.$store.getters.thickness,
          scale: this.$store.getters.scale,
          samples: this.$store.getters.aoSamples,
          distanceFallOff: this.$store.getters.distanceFallOff,
          screenSpaceRadius: false,
        };
        
        this.aoManager.updateGTAOParams(aoParameters);
      }
    },

    // 重置自动恢复计时器（用户操作时调用）
    resetAutoTimer() {
      if (this.$store.getters.autoResetCamera) {
        this.startAutoResetTimer();
      }
    },

    // 手动相机动画设置
    animateCameraToPosition(duration = 2000) {
      if (!this.camera || !this.controls) return;
      
      // 初始相机位置和目标点
      const initialPosition = new THREE.Vector3(300, 200, -300);
      const initialTarget = new THREE.Vector3(0, 0, 0);
      
      // 最终相机位置和目标点
      const finalPosition = new THREE.Vector3(27, 1.5, 27);
      const finalTarget = new THREE.Vector3(8, 12, 0);
      
      // 设置初始位置
      this.camera.position.copy(initialPosition);
      this.controls.target.copy(initialTarget);
      this.controls.update();
      
      const startTime = Date.now();
      
      const animateCamera = () => {
        const elapsedTime = Date.now() - startTime;
        const progress = Math.min(elapsedTime / duration, 1);
        
        // 使用缓动函数
        const easeProgress = this.easeInOutCubic(progress);
        
        // 插值计算当前位置和目标点
        this.camera.position.lerpVectors(initialPosition, finalPosition, easeProgress);
        this.controls.target.lerpVectors(initialTarget, finalTarget, easeProgress);
        this.controls.update();
        
        if (progress < 1) {
          requestAnimationFrame(animateCamera);
        }
      }
      
      animateCamera();
    },

    // 初始化场景
    initScene() {
      this.$emit('loading-progress', { progress: 10, message: '初始化3D场景...' });
      this.scene = new THREE.Scene();
      const sceneContainer = this.$refs.sceneContainer;
      const canvas = this.$refs.canvas;
      const { clientWidth, clientHeight } = sceneContainer;
      this.camera = new THREE.PerspectiveCamera(45, clientWidth / clientHeight, 0.1, 1000);
      this.renderer = new THREE.WebGLRenderer({
        canvas: canvas,
        antialias: true,
        alpha: true,
        logarithmicDepthBuffer: false,
        powerPreference: 'high-performance'
      });
      this.renderer.setSize(clientWidth, clientHeight);
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = THREE.PCFShadowMap;
      this.renderer.shadowMap.autoUpdate = true;
      this.renderer.capabilities.isWebGL2 = true;
      this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = 1.2;
      this.renderer.outputColorSpace = THREE.SRGBColorSpace;
      this.renderer.physicallyCorrectLights = false;
      this.renderer.info.autoReset = false;
      this.createGradientSky();
      this.$emit('loading-progress', { progress: 30, message: '加载HDR环境贴图...' });
      const rgbeLoader = new RGBELoader();
      rgbeLoader.setPath('/HDR/');
      rgbeLoader.load('studio_small_09_4k.hdr', (texture) => {
        texture.mapping = THREE.EquirectangularReflectionMapping;
        this.scene.environment = texture;
        this.$emit('loading-progress', { progress: 50, message: '加载3D模型...' });
        this.loadModel();
      });
      this.setupLighting();
      this.createShadowGround();
      this.controls = new OrbitControls(this.camera, this.renderer.domElement);
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.1;
      this.controls.minDistance = 5;
      this.controls.maxDistance = 1000;
      this.controls.addEventListener('start', () => {
        this.resetAutoTimer();
        this.requestRender();
      });
      this.controls.addEventListener('change', () => {
        this.resetAutoTimer();
        this.requestRender();
      });
      this.controls.addEventListener('end', () => {
        this.requestRender();
      });
      let lastValidPosition = null;
      let lastValidTarget = null;
      let lastCheckTime = 0;
      this.controls.addEventListener('change', () => {
        const now = Date.now();
        if (now - lastCheckTime < 16) return;
        lastCheckTime = now;
        if (this.camera.position.y <= 1) {
          if (lastValidPosition && lastValidTarget) {
            this.camera.position.copy(lastValidPosition);
            this.controls.target.copy(lastValidTarget);
            this.controls.update();
          } else {
            this.camera.position.y = 1;
            this.controls.update();
          }
        } else {
          lastValidPosition = this.camera.position.clone();
          lastValidTarget = this.controls.target.clone();
        }
      });
      this.initAOManager();
    },

    // 初始化AO管理器
    initAOManager() {
      this.aoManager = new AOManager();
      
      // 使用GTAO低质量预设配置
      const config = AOManager.getPreset('low');
      
      // 根据store设置启用状态
      config.enableAO = this.$store.getters.enableAO;
      
      // 使用store中的参数覆盖预设配置
      config.output = this.$store.getters.gtaoOutput;
      config.blendIntensity = this.$store.getters.blendIntensity;
      
      config.aoParameters = {
        radius: this.$store.getters.aoRadius,
        distanceExponent: this.$store.getters.distanceExponent,
        thickness: this.$store.getters.thickness,
        scale: this.$store.getters.scale,
        samples: this.$store.getters.aoSamples,
        distanceFallOff: this.$store.getters.distanceFallOff,
        screenSpaceRadius: false,
      };
      
      // 初始化AO管理器
      this.aoManager.init(this.renderer, this.scene, this.camera, config);
    },

    // 设置光照
    setupLighting() {
      const isDayMode = this.$store.getters.isDayMode;
      
      // 环境光
      const ambientLight = new THREE.AmbientLight(
        isDayMode ? 0xffffff : 0x4a5a8a, 
        isDayMode ? 0.3 : 0.15
      );
      this.scene.add(ambientLight);

      // 主光源
      const mainLight = new THREE.DirectionalLight(
        isDayMode ? 0xe6f3ff : 0x9bb5ff, 
        isDayMode ? 1.5 : 0.25
      );
      mainLight.position.set(isDayMode ? 10 : -5, isDayMode ? 20 : 15, isDayMode ? 8 : 12);
      mainLight.castShadow = true;
      
      // 统一阴影设置
      mainLight.shadow.mapSize.width = 1024;
      mainLight.shadow.mapSize.height = 1024;
      mainLight.shadow.camera.near = 0.1;
      mainLight.shadow.camera.far = 100;
      mainLight.shadow.camera.left = -30;
      mainLight.shadow.camera.right = 30;
      mainLight.shadow.camera.top = 30;
      mainLight.shadow.camera.bottom = -30;
      mainLight.shadow.bias = -0.0005;
      mainLight.shadow.normalBias = 0.05;
      mainLight.shadow.radius = 1;
      mainLight.shadow.blurSamples = 1;
      this.scene.add(mainLight);

      // 补光
      const fillLight = new THREE.DirectionalLight(
        isDayMode ? 0xffe4b5 : 0x6a7fdb, 
        isDayMode ? 0.15 : 0.1
      );
      fillLight.position.set(isDayMode ? -10 : 8, 12, isDayMode ? -8 : -10);
      fillLight.castShadow = false;
      this.scene.add(fillLight);

      // 夜晚额外的边缘光
      if (!isDayMode) {
        const rimLight = new THREE.DirectionalLight(0x4a6fa5, 0.1);
        rimLight.position.set(15, 5, -15);
        rimLight.castShadow = false;
        this.scene.add(rimLight);
      }
    },

    // 创建渐变天空背景
    createGradientSky() {
      const skyGeometry = new THREE.SphereGeometry(500, 32, 32);
      
      const skyMaterial = new THREE.ShaderMaterial({
        uniforms: {
          topColor: { value: new THREE.Color() },
          middleColor: { value: new THREE.Color() },
          bottomColor: { value: new THREE.Color() },
          horizonRange: { value: 0.3 },
          offset: { value: 33 }
        },
        vertexShader: `
          varying vec3 vWorldPosition;
          void main() {
            vec4 worldPosition = modelMatrix * vec4(position, 1.0);
            vWorldPosition = worldPosition.xyz;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: `
          uniform vec3 topColor;
          uniform vec3 middleColor;
          uniform vec3 bottomColor;
          uniform float horizonRange;
          uniform float offset;
          varying vec3 vWorldPosition;
          
          void main() {
            float h = normalize(vWorldPosition + offset).y;
            
            // 三段式渐变：顶部 -> 中部 -> 底部（地平线）
            vec3 color;
            
            if (h > horizonRange) {
              // 上半部分：顶部到中部的渐变
              float t = (h - horizonRange) / (1.0 - horizonRange);
              color = mix(middleColor, topColor, smoothstep(0.0, 1.0, t));
            } else {
              // 下半部分：中部到底部的渐变
              float t = h / horizonRange;
              color = mix(bottomColor, middleColor, smoothstep(0.0, 1.0, t));
            }
            
            gl_FragColor = vec4(color, 1.0);
          }
        `,
        side: THREE.BackSide
      });
      
      const sky = new THREE.Mesh(skyGeometry, skyMaterial);
      sky.name = 'gradientSky';
      this.scene.add(sky);
      
      // 设置初始颜色
      this.updateSkyColors(this.$store.getters.isDayMode, false);
    },

    // 更新天空颜色
    updateSkyColors(isDayMode, animate = false) {
      const sky = this.scene.getObjectByName('gradientSky');
      if (!sky || !sky.material) return;
      
      const targetTopColor = new THREE.Color();
      const targetMiddleColor = new THREE.Color();
      const targetBottomColor = new THREE.Color();
      let targetHorizonRange;
      
      if (isDayMode) {
        const dayColors = this.$store.getters.dayModeColors;
        targetTopColor.set(dayColors.skyTop);
        targetMiddleColor.set(dayColors.skyMiddle);
        targetBottomColor.set(dayColors.skyBottom);
        targetHorizonRange = dayColors.horizonRange;
      } else {
        const nightColors = this.$store.getters.nightModeColors;
        targetTopColor.set(nightColors.skyTop);
        targetMiddleColor.set(nightColors.skyMiddle);
        targetBottomColor.set(nightColors.skyBottom);
        targetHorizonRange = nightColors.horizonRange;
      }
      
      if (!animate) {
        sky.material.uniforms.topColor.value.copy(targetTopColor);
        sky.material.uniforms.middleColor.value.copy(targetMiddleColor);
        sky.material.uniforms.bottomColor.value.copy(targetBottomColor);
        sky.material.uniforms.horizonRange.value = targetHorizonRange;
        return;
      }
      
      // 停止之前的动画
      if (sky.userData.colorAnimationId) {
        cancelAnimationFrame(sky.userData.colorAnimationId);
      }
      
      // 保存起始颜色和参数
      const startTopColor = sky.material.uniforms.topColor.value.clone();
      const startMiddleColor = sky.material.uniforms.middleColor.value.clone();
      const startBottomColor = sky.material.uniforms.bottomColor.value.clone();
      const startHorizonRange = sky.material.uniforms.horizonRange.value;
      
      const startTime = Date.now();
      const duration = 2000; // 2秒动画
      
      const animateColors = () => {
        const elapsedTime = Date.now() - startTime;
        const progress = Math.min(elapsedTime / duration, 1);
        const easeProgress = this.easeInOutCubic(progress);
        
        sky.material.uniforms.topColor.value.lerpColors(startTopColor, targetTopColor, easeProgress);
        sky.material.uniforms.middleColor.value.lerpColors(startMiddleColor, targetMiddleColor, easeProgress);
        sky.material.uniforms.bottomColor.value.lerpColors(startBottomColor, targetBottomColor, easeProgress);
        sky.material.uniforms.horizonRange.value = startHorizonRange + (targetHorizonRange - startHorizonRange) * easeProgress;
        
        if (progress < 1) {
          sky.userData.colorAnimationId = requestAnimationFrame(animateColors);
        } else {
          sky.userData.colorAnimationId = null;
        }
      };
      
      animateColors();
    },

    // 缓动函数
    easeInOutCubic(t) {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    },
    easeOutCubic(t) {
      return 1 - Math.pow(1 - t, 3);
    },

    // 创建接收阴影的地面
    createShadowGround() {
      const groundGeometry = new THREE.PlaneGeometry(1000, 1000, 32, 32);
      
      // 创建canvas和材质，稍后会更新纹理
      const canvas = document.createElement('canvas');
      canvas.width = 256;
      canvas.height = 256;
      
      // 创建纹理
      const texture = new THREE.CanvasTexture(canvas);
      texture.wrapS = THREE.ClampToEdgeWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      
      const groundMaterial = new THREE.MeshLambertMaterial({
        map: texture,
        transparent: true,
        opacity: 1.0
      });
      
      const groundPlane = new THREE.Mesh(groundGeometry, groundMaterial);
      groundPlane.rotation.x = -Math.PI / 2;
      groundPlane.position.y = -0.01;
      groundPlane.receiveShadow = true;
      groundPlane.name = 'shadowGround';
      
      // 设置userData标记，让地面不参与AO计算
      groundPlane.userData.ignoreAO = true;
      groundPlane.userData.excludeFromAO = true;
      
      groundMaterial.userData = groundMaterial.userData || {};
      groundMaterial.userData.ignoreAO = true;
      
      this.scene.add(groundPlane);
      
      // 初始化地面颜色和透明度
      this.updateGroundColor(false);
    },

    // 更新地面颜色和透明度
    updateGroundColor(animate = false) {
      const ground = this.scene.getObjectByName('shadowGround');
      if (!ground || !ground.material || !ground.material.map) return;
      
      const canvas = ground.material.map.image;
      const ctx = canvas.getContext('2d');
      
      const isDayMode = this.$store.getters.isDayMode;
      const targetOpacity = isDayMode ? 1.0 : 0.5;
      
      if (!animate) {
        // 直接设置颜色和透明度（初始化时使用）
        ctx.clearRect(0, 0, 256, 256);
        const gradient = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
        const groundColorHex = isDayMode ? this.$store.getters.dayModeColors.groundColor : this.$store.getters.nightModeColors.groundColor;
        const groundColor = new THREE.Color(groundColorHex);
        const groundColorRGB = `${Math.round(groundColor.r * 255)}, ${Math.round(groundColor.g * 255)}, ${Math.round(groundColor.b * 255)}`;
        gradient.addColorStop(0, `rgba(${groundColorRGB}, ${targetOpacity})`);
        gradient.addColorStop(1, `rgba(${groundColorRGB}, 0)`);
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, 256, 256);
        ground.material.map.needsUpdate = true;
        return;
      }
      
      // 停止之前的动画
      if (ground.userData.opacityAnimationId) {
        cancelAnimationFrame(ground.userData.opacityAnimationId);
      }
      
      // 保存起始透明度
      const startOpacity = ground.userData.currentOpacity || (isDayMode ? 0.5 : 1.0);
      
      const startTime = Date.now();
      const duration = 2000; // 2秒动画，与天空动画同步
      
      const animateOpacity = () => {
        const elapsedTime = Date.now() - startTime;
        const progress = Math.min(elapsedTime / duration, 1);
        const easeProgress = this.easeInOutCubic(progress);
        
        // 插值计算当前透明度
        const currentOpacity = startOpacity + (targetOpacity - startOpacity) * easeProgress;
        
        // 更新地面纹理
        ctx.clearRect(0, 0, 256, 256);
        const gradient = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
        const groundColorHex = isDayMode ? this.$store.getters.dayModeColors.groundColor : this.$store.getters.nightModeColors.groundColor;
        const groundColor = new THREE.Color(groundColorHex);
        const groundColorRGB = `${Math.round(groundColor.r * 255)}, ${Math.round(groundColor.g * 255)}, ${Math.round(groundColor.b * 255)}`;
        gradient.addColorStop(0, `rgba(${groundColorRGB}, ${currentOpacity})`);
        gradient.addColorStop(1, `rgba(${groundColorRGB}, 0)`);
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, 256, 256);
        ground.material.map.needsUpdate = true;
        
        // 保存当前透明度
        ground.userData.currentOpacity = currentOpacity;
        
        if (progress < 1) {
          ground.userData.opacityAnimationId = requestAnimationFrame(animateOpacity);
        } else {
          ground.userData.opacityAnimationId = null;
        }
      };
      
      animateOpacity();
    },

    // 更新场景颜色（响应颜色设置变化）
    updateSceneColors() {
      if (!this.scene) return;
      
      const isDayMode = this.$store.getters.isDayMode;
      
      // 更新天空渐变颜色
      this.updateSkyColors(isDayMode, false);
      
      // 更新地面颜色
      this.updateGroundColor(false);
    },

    // 更新白天黑夜模式
    updateDayNightMode(isDayMode) {
      if (!this.scene) return;
      
      // 更新天空渐变颜色
      this.updateSkyColors(isDayMode, true);
      
      // 更新地面颜色和透明度
      this.updateGroundColor(true);
      
      // 收集所有需要动画的光源
      const lightsToAnimate = [];
      this.scene.traverse((child) => {
        if (child.isAmbientLight || child.isDirectionalLight) {
          lightsToAnimate.push(child);
        }
      });
      
      // 停止之前的光照动画
      if (this.scene.userData.lightAnimationId) {
        cancelAnimationFrame(this.scene.userData.lightAnimationId);
      }
      
      // 保存起始状态
      const startStates = lightsToAnimate.map(light => ({
        light: light,
        startIntensity: light.intensity,
        startColor: light.color.clone(),
        targetIntensity: 0,
        targetColor: new THREE.Color()
      }));
      
      // 设置目标状态
      startStates.forEach(state => {
        const { light } = state;
        if (light.isAmbientLight) {
          state.targetIntensity = isDayMode ? 0.3 : 0.15;
          state.targetColor.setHex(isDayMode ? 0xffffff : 0x4a5a8a);
        } else if (light.isDirectionalLight) {
          if (light.position.y > 0) {
            // 主光源
            state.targetIntensity = isDayMode ? 1.5 : 0.25;
            state.targetColor.setHex(isDayMode ? 0xe6f3ff : 0x9bb5ff);
          } else {
            // 补光和边缘光
            if (light.position.x > 0) {
              state.targetIntensity = isDayMode ? 0.15 : 0.1;
              state.targetColor.setHex(isDayMode ? 0xffe4b5 : 0x6a7fdb);
            } else {
              state.targetIntensity = isDayMode ? 0 : 0.1;
              state.targetColor.setHex(0x4a6fa5);
            }
          }
        }
      });
      
      // 渲染器设置动画
      const startExposure = this.renderer ? this.renderer.toneMappingExposure : 1.0;
      const targetExposure = isDayMode ? 1.4 : 0.5;
      
      const startTime = Date.now();
      const duration = 2000;
      
      const animateLighting = () => {
        const elapsedTime = Date.now() - startTime;
        const progress = Math.min(elapsedTime / duration, 1);
        const easeProgress = this.easeInOutCubic(progress);
        
        // 动画光照
        startStates.forEach(state => {
          const { light, startIntensity, startColor, targetIntensity, targetColor } = state;
          light.intensity = startIntensity + (targetIntensity - startIntensity) * easeProgress;
          light.color.lerpColors(startColor, targetColor, easeProgress);
        });
        
        // 动画渲染器曝光
        if (this.renderer) {
          this.renderer.toneMappingExposure = startExposure + (targetExposure - startExposure) * easeProgress;
        }
        
        // 更新阴影设置
        const mainLight = lightsToAnimate.find(light => light.isDirectionalLight && light.position.y > 0);
        if (mainLight) {
          mainLight.shadow.bias = -0.0005;
          mainLight.shadow.normalBias = 0.05;
          mainLight.shadow.radius = 1;
          mainLight.shadow.blurSamples = 1;
        }
          
        if (progress < 1) {
          this.scene.userData.lightAnimationId = requestAnimationFrame(animateLighting);
        } else {
          this.scene.userData.lightAnimationId = null;
        }
      };
      
      animateLighting();
      
      // 更新环境贴图强度
      if (this.model) {
        this.model.traverse((node) => {
          if (node.isMesh && node.material) {
            if (Array.isArray(node.material)) {
              node.material.forEach(mat => {
                mat.envMapIntensity = isDayMode ? 2.2 : 0.8;
                mat.needsUpdate = true;
              });
            } else {
              node.material.envMapIntensity = isDayMode ? 2.2 : 0.8;
              node.material.needsUpdate = true;
            }
          }
        });
      }
    },

    // 更新梯子位置
    updateLadderPosition(isUp) {
      if (!this.model) return;
      
      // 查找骨骼002
      let bone002 = null;
      this.model.traverse((node) => {
        if (node.isSkinnedMesh && node.skeleton) {
          const bones = node.skeleton.bones;
          bone002 = bones.find(bone => bone.name === '骨骼002' || bone.name === 'bone002' || bone.name === 'Bone002');
        }
      });
      
      if (bone002) {
        // 保存初始旋转角度
        if (bone002.userData.initialRotationZ === undefined) {
          bone002.userData.initialRotationZ = bone002.rotation.z;
        }
        
        // 停止之前的动画
        if (bone002.userData.animationId) {
          cancelAnimationFrame(bone002.userData.animationId);
        }
        
        // 计算目标角度
        const additionalAngle = isUp ? -51 : 0;
        const targetRotation = bone002.userData.initialRotationZ + (additionalAngle * Math.PI) / 180;
        const startRotation = bone002.rotation.z;
        const startTime = Date.now();
        const duration = 1000;
        
        // 动画函数
        const animateLadder = () => {
          const elapsedTime = Date.now() - startTime;
          const progress = Math.min(elapsedTime / duration, 1);
          
          // 使用缓动函数
          const easeProgress = this.easeOutCubic(progress);
          
          bone002.rotation.z = startRotation + (targetRotation - startRotation) * easeProgress;
          
          if (progress < 1) {
            bone002.userData.animationId = requestAnimationFrame(animateLadder);
          } else {
            bone002.userData.animationId = null;
          }
        };
        
        // 缓动函数
        function easeOutCubic(t) {
          return 1 - Math.pow(1 - t, 3);
        }
        
        animateLadder();
      }
    },

    // 更新平台旋转
    updatePlatformRotation(angle) {
      if (!this.model) return;
      
      // 查找骨骼001
      let bone001 = null;
      this.model.traverse((node) => {
        if (node.isSkinnedMesh && node.skeleton) {
          const bones = node.skeleton.bones;
          bone001 = bones.find(bone => bone.name === '骨骼001' || bone.name === 'bone001' || bone.name === 'Bone001');
        }
      });
      
      if (bone001) {
        const radians = (angle * Math.PI) / 180;
        bone001.rotation.y = radians;
      }
    },

    // 更新推压斗杆伸出长度
    updateArmExtension(length) {
      if (!this.model) return;
      
      // 查找骨骼007
      let bone007 = null;
      this.model.traverse((node) => {
        if (node.isSkinnedMesh && node.skeleton) {
          const bones = node.skeleton.bones;
          bone007 = bones.find(bone => bone.name === '骨骼007' || bone.name === 'bone007' || bone.name === 'Bone007');
        }
      });
      
      if (bone007) {
        // 如果是第一次调用，记录初始位置
        if (this.bone007InitialX === null) {
          this.bone007InitialX = bone007.position.x;
        }
        
        // 设置骨骼007的局部X轴位置
        bone007.position.x = this.bone007InitialX - (length - 6);
      }
    },

    // 更新提升下放长度
    updateLiftHeight(height) {
      if (!this.model) return;
      this.bone007height = height;
      
      // 更新天轮旋转
      this.updateWheelRotation(height);
      
      // 查找骨骼007
      let bone007 = null;
      this.model.traverse((node) => {
        if (node.isSkinnedMesh && node.skeleton) {
          const bones = node.skeleton.bones;
          bone007 = bones.find(bone => bone.name === '骨骼007' || bone.name === 'bone007' || bone.name === 'Bone007');
        }
      });
      
      if (bone007) {
        // 如果是第一次调用，记录初始位置
        if (this.bone007InitialY === null) {
          this.bone007InitialY = bone007.position.y;
        }
        
        const offset = height - 3;
        bone007.position.y = this.bone007InitialY + offset;
      }
    },

    // 更新天轮旋转
    updateWheelRotation(currentHeight) {
      if (!this.wheelObject) return;
      
      const deltaHeight = currentHeight - this.lastLiftHeight;
      
      // 只有当高度有变化时才旋转
      if (Math.abs(deltaHeight) > 0.001) {
        // 天轮转动比例：绳子变化5.6米转动360度(2π弧度)
        // 转动角度 = (绳索长度变化 / 5.6) * 2π
        const rotationDelta = (deltaHeight / 5.6) * 2 * Math.PI * 0.25;
        
        // 累加旋转角度
        this.wheelRotation += rotationDelta;
        
        // 应用旋转到天轮对象（绕Z轴旋转）
        this.wheelObject.rotation.z = this.wheelRotation;
        
        const direction = deltaHeight > 0 ? '顺时针' : '逆时针';
        const rotationDegrees = rotationDelta * 180 / Math.PI;
        const totalRotationDegrees = this.wheelRotation * 180 / Math.PI;
        console.log(`天轮旋转: 高度变化=${deltaHeight.toFixed(3)}m, 方向=${direction}, 旋转角度=${rotationDegrees.toFixed(1)}°, 累计旋转=${totalRotationDegrees.toFixed(1)}°`);
        console.log(`计算验证: 0.1m变化应转动=${(0.1/5.6*360).toFixed(1)}°, 5.6m变化转动=${(5.6/5.6*360).toFixed(0)}°`);
      }
      
      // 更新记录的值
      this.lastLiftHeight = currentHeight;
    },

    // 重置天轮旋转
    resetWheelRotation() {
      if (this.wheelObject) {
        this.wheelRotation = 0;
        this.wheelObject.rotation.z = 0;
        this.lastLiftHeight = 3;
        console.log('天轮旋转已重置');
      }
    },



    // 加载模型
    loadModel() {
      const loader = new GLTFLoader();
      
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
      dracoLoader.setDecoderConfig({ type: 'js' });
      loader.setDRACOLoader(dracoLoader);
      
      // 添加备用进度更新定时器
      let progressTimer = null;
      let currentProgress = 50;
      const startProgressTimer = () => {
        progressTimer = setInterval(() => {
          if (currentProgress < 75) {
            currentProgress += 2;
            this.$emit('loading-progress', { 
              progress: currentProgress, 
              message: '正在加载模型文件...' 
            });
          }
        }, 500);
      };
      
      const clearProgressTimer = () => {
        if (progressTimer) {
          clearInterval(progressTimer);
          progressTimer = null;
        }
      };
      
      // 启动备用进度定时器
      startProgressTimer();
      
      loader.load('/models/wk35v3-250715.glb', (gltf) => {
        // 清除备用进度定时器
        clearProgressTimer();
        
        // 模型加载成功
        this.$emit('loading-progress', { progress: 100, message: '场景加载完成！' });
        
        this.model = gltf.scene;
        this.scene.add(this.model);
        
        this.animations = gltf.animations;
        
        // 设置模型属性并查找天轮对象
        this.model.traverse((node) => {
          if (node.isMesh) {
            node.castShadow = true;
            node.receiveShadow = true;
            
            // 查找天轮对象（根据名称匹配）
            if (node.name && (
              node.name.includes('天轮') || 
              node.name.includes('wheel') || 
              node.name.includes('Wheel') || 
              node.name.includes('滑轮') ||
              node.name.includes('轮') ||
              node.name.toLowerCase().includes('pulley')
            )) {
              this.wheelObject = node;
              console.log('找到天轮对象:', node.name);
            }
            
            // 重新计算法线
            if (node.geometry) {
              node.geometry.computeVertexNormals();
            }
            
            if (node.material) {
              if (Array.isArray(node.material)) {
                node.material.forEach(mat => {
                  mat.envMapIntensity = 1.5;
                  mat.flatShading = false;
                  mat.needsUpdate = true;
                });
              } else {
                node.material.envMapIntensity = 1.5;
                node.material.flatShading = false;
                node.material.needsUpdate = true;
              }
            }
          }
        });
        
        // 启动相机动画
        this.animateCameraToPosition();
        
        this.initSkeleton();
        this.initAnimations();
        this.initIK(gltf);
        
        // 发射场景准备完成事件
        this.$emit('scene-ready');
      }, 
      (xhr) => {
        // Model loading progress
        if (xhr.lengthComputable) {
          const percentComplete = (xhr.loaded / xhr.total) * 100;
          const progress = 50 + (percentComplete * 0.3);
          this.$emit('loading-progress', { 
            progress: Math.min(progress, 80), 
            message: `加载模型: ${Math.round(percentComplete)}%` 
          });
        } else {
          this.$emit('loading-progress', { 
            progress: 65, 
            message: '正在加载模型文件...' 
          });
        }
      },
      (error) => {
        clearProgressTimer();
        console.error('模型加载失败:', error);
        this.$emit('loading-progress', { progress: 100, message: '模型加载失败' });
      });
    },

    // 初始化骨骼系统
    initSkeleton() {
      if (!this.model) return;
      
      let skinnedMesh = null;
      this.model.traverse((node) => {
        if (node.isSkinnedMesh) {
          skinnedMesh = node;
        }
      });
      
      if (skinnedMesh && skinnedMesh.skeleton) {
        this.skeletonHelper = new SkeletonHelper(skinnedMesh);
        this.skeletonHelper.material.linewidth = 2;
        this.skeletonHelper.visible = false;
        this.scene.add(this.skeletonHelper);
      }
    },

    // 初始化动画系统
    initAnimations() {
      if (!this.model || !this.animations.length) return;
      
      this.mixer = new THREE.AnimationMixer(this.model);
      
      this.animations.forEach((clip, index) => {
        const action = this.mixer.clipAction(clip);
        // 不自动播放，等待用户手动控制
        // if (index === 0) {
        //   action.play();
        // }
      });
    },

    // 初始化IK系统
    initIK(gltf) {
      if (!this.model) return;
      
      let skinnedMesh = null;
      this.model.traverse((node) => {
        if (node.isSkinnedMesh) {
          skinnedMesh = node;
        }
      });
      
      if (skinnedMesh && skinnedMesh.skeleton) {
        const bones = skinnedMesh.skeleton.bones;
        
        let ikChains = [];
        if (gltf?.userData?.ikConstraints) {
          ikChains = gltf.userData.ikConstraints;
        }
        
        const extensions = gltf.parser?.json?.extensions || {};
        Object.keys(extensions).forEach(extName => {
          if (extName.includes('constraint') || extName.includes('limit') || extName.includes('ik')) {
            // Process constraint extensions
          }
        });
        
        if (ikChains.length > 0) {
          try {
            this.ikSolver = new CCDIKSolver(skinnedMesh, ikChains);
          } catch (error) {
            this.ikSolver = null;
          }
        }
      }
      
      // 初始化自定义lookAt约束系统
      this.initLookAtConstraints();
    },

    initLookAtConstraints() {
      if (!this.model) return;
      let skinnedMesh = null;
      this.model.traverse((node) => {
        if (node.isSkinnedMesh) {
          skinnedMesh = node;
        }
      });
      if (skinnedMesh && skinnedMesh.skeleton) {
        const bones = skinnedMesh.skeleton.bones;
        // 查找所需的骨骼
        const bone004 = bones.find(bone => bone.name === '骨骼004' || bone.name === 'bone004' || bone.name === 'Bone004');
        const bone003 = bones.find(bone => bone.name === '骨骼003' || bone.name === 'bone003' || bone.name === 'Bone003');
        const bone006 = bones.find(bone => bone.name === '骨骼006' || bone.name === 'bone006' || bone.name === 'Bone006');
        const bone008 = bones.find(bone => bone.name === '骨骼008' || bone.name === 'bone008' || bone.name === 'Bone008');
        const bone005 = bones.find(bone => bone.name === '骨骼005' || bone.name === 'bone005' || bone.name === 'Bone005');
        const bone009 = bones.find(bone => bone.name === '骨骼009' || bone.name === 'bone009' || bone.name === 'Bone009');
        // 计算每个骨骼的初始朝向方向
        const getInitialForward = (sourceBone, targetBone) => {
          if (!sourceBone || !targetBone) return new THREE.Vector3(0, 0, 1);
          const sourcePos = new THREE.Vector3();
          const targetPos = new THREE.Vector3();
          sourceBone.getWorldPosition(sourcePos);
          targetBone.getWorldPosition(targetPos);
          const initialDirection = new THREE.Vector3();
          initialDirection.subVectors(targetPos, sourcePos).normalize();
          const localDirection = new THREE.Vector3();
          if (sourceBone.parent) {
            const parentInverseMatrix = new THREE.Matrix4();
            parentInverseMatrix.copy(sourceBone.parent.matrixWorld).invert();
            localDirection.copy(initialDirection);
            localDirection.transformDirection(parentInverseMatrix);
          } else {
            localDirection.copy(initialDirection);
          }
          return localDirection;
        };
        // 设置lookAt约束关系
        this.lookAtConstraints = [];
        if (bone004 && bone003) {
          this.lookAtConstraints.push({
            source: bone004,
            target: bone003,
            name: '骨骼004 -> 骨骼003',
            initialQuaternion: bone004.quaternion.clone(),
            initialForward: getInitialForward(bone004, bone003)
          });
        }
        if (bone006 && bone008) {
          this.lookAtConstraints.push({
            source: bone006,
            target: bone008,
            name: '骨骼006 -> 骨骼008',
            initialQuaternion: bone006.quaternion.clone(),
            initialForward: getInitialForward(bone006, bone008)
          });
        }
        if (bone005 && bone009) {
          this.lookAtConstraints.push({
            source: bone005,
            target: bone009,
            name: '骨骼005 -> 骨骼009',
            initialQuaternion: bone005.quaternion.clone(),
            initialForward: getInitialForward(bone005, bone009)
          });
        }
      }
    },

    // 更新lookAt约束 - 基于初始状态应用约束偏移
    updateLookAtConstraints() {
      if (this.lookAtConstraints.length === 0) return;
      
      this.lookAtConstraints.forEach(constraint => {
        const { source, target, initialQuaternion, initialForward } = constraint;
        
        if (source && target && initialQuaternion && initialForward) {
          // 获取源骨骼和目标骨骼的世界位置
          const sourceWorldPosition = new THREE.Vector3();
          const targetWorldPosition = new THREE.Vector3();
          
          source.getWorldPosition(sourceWorldPosition);
          target.getWorldPosition(targetWorldPosition);
          
          // 计算从源到目标的方向向量
          const direction = new THREE.Vector3();
          direction.subVectors(targetWorldPosition, sourceWorldPosition);
          
          if (direction.length() < 0.001) {
            // 距离太近时保持初始状态
            source.quaternion.copy(initialQuaternion);
            return;
          }
          
          direction.normalize();
          
          // 将方向向量转换到源骨骼的局部坐标系
          const localDirection = new THREE.Vector3();
          if (source.parent) {
            const parentInverseMatrix = new THREE.Matrix4();
            parentInverseMatrix.copy(source.parent.matrixWorld).invert();
            localDirection.copy(direction);
            localDirection.transformDirection(parentInverseMatrix);
          } else {
            localDirection.copy(direction);
          }
          
          // 使用每个骨骼自己的初始朝向方向计算约束旋转
          const constraintRotation = new THREE.Quaternion();
          
          // 计算从骨骼初始朝向到当前目标方向的旋转
          const constraintAxis = new THREE.Vector3().crossVectors(initialForward, localDirection);
          const constraintAngle = initialForward.angleTo(localDirection);
          
          if (constraintAxis.length() > 0.001) {
            constraintAxis.normalize();
            constraintRotation.setFromAxisAngle(constraintAxis, constraintAngle); // 约束强度为1.0
          }
          
          // 将约束旋转应用到初始状态上
          source.quaternion.multiplyQuaternions(initialQuaternion, constraintRotation);
        }
      });
    },



    // 渲染性能优化
    // 请求渲染
    requestRender() {
      if (!this.needsRender) {
        this.needsRender = true;
        if (!this.isAnimating) {
          this.startAnimation();
        }
      }
      
      clearTimeout(this.renderTimeout);
      this.renderTimeout = setTimeout(() => {
        this.needsRender = false;
      }, 1500);
    },

    // 检查是否需要渲染
    shouldRender() {
      if (!this.camera || !this.controls) return false;
      
      // 检查相机位置是否改变
      const currentPosition = this.camera.position.clone();
      const currentTarget = this.controls.target.clone();
      
      const positionChanged = !currentPosition.equals(this.lastCameraPosition);
      const targetChanged = !currentTarget.equals(this.lastCameraTarget);
      
      if (positionChanged || targetChanged) {
        this.lastCameraPosition.copy(currentPosition);
        this.lastCameraTarget.copy(currentTarget);
        return true;
      }
      
      // 检查是否有动画在运行
      if (this.mixer && this.mixer._actions.some(action => action.isRunning())) {
        return true;
      }
      
      // 检查是否有自定义动画
      if (this.scene && this.scene.userData.lightAnimationId) {
        return true;
      }
      
      // 检查天空动画
      const sky = this.scene?.getObjectByName('gradientSky');
      if (sky && sky.userData.colorAnimationId) {
        return true;
      }
      
      // 检查地面颜色动画
      const ground = this.scene?.getObjectByName('shadowGround');
      if (ground && ground.userData.opacityAnimationId) {
        return true;
      }
      
      return this.needsRender;
    },

    animate() {
      if (!this.shouldRender()) {
        this.isAnimating = false;
        return;
      }
      
      this.animationFrameId = requestAnimationFrame(this.animate);
      
      this.frameCount++;
      const deltaTime = this.clock.getDelta();
      
      if (this.mixer) {
        this.mixer.update(deltaTime);
      }
      
      if (this.ikSolver) {
        this.ikSolver.update();
      }
      
      // 更新自定义lookAt约束
      this.updateLookAtConstraints();
      
      if (this.controls) {
        this.controls.update();
      }

      // 使用AO管理器渲染
      if (this.aoManager) {
        this.aoManager.render();
      } else {
        this.renderer?.render(this.scene, this.camera);
      }
    },

    startAnimation() {
      if (!this.isAnimating) {
        this.isAnimating = true;
        this.animate();
      }
    },

    handleResize() {
      if (!this.$refs.sceneContainer || !this.camera || !this.renderer) return;
      
      const { clientWidth, clientHeight } = this.$refs.sceneContainer;
      this.camera.aspect = clientWidth / clientHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(clientWidth, clientHeight);
      
      // 更新AO管理器尺寸
      if (this.aoManager) {
        this.aoManager.handleResize(clientWidth, clientHeight);
      }
      
      this.requestRender();
    },

    onMounted() {
      this.initScene();
      this.startAnimation();
      window.addEventListener('resize', this.handleResize);
    },

    onBeforeUnmount() {
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
      }
      
      if (this.renderTimeout) {
        clearTimeout(this.renderTimeout);
      }
      
      if (this.mixer) {
        this.mixer.stopAllAction();
        this.mixer = null;
      }
      
      if (this.skeletonHelper) {
        this.scene?.remove(this.skeletonHelper);
        this.skeletonHelper = null;
      }
      
      this.ikSolver = null;
      
      // 清理lookAt约束系统
      this.lookAtConstraints = [];
      
      // 清理天轮对象引用
      this.wheelObject = null;
      
      // 清理AO管理器
      if (this.aoManager) {
        this.aoManager.dispose();
        this.aoManager = null;
      }
      
      this.renderer?.dispose();
      this.scene?.clear();
      this.controls?.dispose();
      window.removeEventListener('resize', this.handleResize);
    }
  }
};
</script>

<style scoped>
.scene-container {
  width: 100%;
  height: 100%;
  position: relative;
}

canvas {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
}
</style>